import { saveAs } from 'file-saver';
import { DownloadIcon, EyeIcon } from '@heroicons/react/outline';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  projectMilestones,
  apiZedocGetCustomPDF,
  apiZedocGetCompletedMilestoneListWithCustomPDF,
} from '../../../common/api/zedoc';
import { default as ProjectMilestoneSelect } from '../../../common/selectors/ProjectMilestone';
import { default as AnswersSheetSelect } from '../../../common/selectors/AnswersSheet';
import { one as oneAnswersSheet } from '../../../common/api/collections/AnswersSheets';
import AnswersSheet from '../../../common/models/AnswersSheet';
import TranslationsSelect from '../../inputs/TranslationsSelect';
import Button from '../../Button';
import CustomPDF from './CustomPDF';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { notifyError } from '../../../utils/notify';
import settings from '../../../common/settings';
import { useRum } from '../../../containers/RumContext';

const {
  features: { enableCustomReports },
} = settings.public;

const DownloadCard = ({ answersSheet, onPreview, onDownload }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [answersSheetLanguage, setAnswersSheetLanguage] = useState();

  const handleOnPreview = onPreview ? () => onPreview(answersSheet) : null;
  const handleOnDownload = (audience) =>
    onDownload({
      references: { answersSheetId: answersSheet._id },
      audience,
      language: answersSheetLanguage,
    });

  const answersSheetId = answersSheet._id;
  useDDPSubscription(
    answersSheetId && oneAnswersSheet.withParams(answersSheetId),
  );
  const answersSheetDocument = useSelector(
    AnswersSheetSelect.one().whereIdEquals(answersSheetId),
  );
  useDDPSubscription(
    answersSheetDocument &&
      projectMilestones.withParams({
        projectId: answersSheetDocument.projectId,
      }),
  );
  const milestone = useSelector(
    ProjectMilestoneSelect.one().whereIdEquals(
      answersSheet && answersSheet.milestoneId,
    ),
  );

  return (
    <div className="cluster-4 items-center justify-between flex-wrap">
      <div>
        <p className="font-semibold">
          {milestone && (
            <span>
              {milestone.name}
              {', '}
            </span>
          )}
          {answersSheet.questionnaireId}
        </p>
        {answersSheet.completedAt && (
          <p className="text-sm text-secondary">
            {`${t('completedAt')} ${toIntlDateTimeFormat(
              language,
              answersSheet.completedAt,
              { dateStyle: 'short', timeStyle: 'short' },
            )}`}
          </p>
        )}
      </div>
      <div className="cluster-4 items-center flex-wrap">
        {handleOnPreview && (
          <>
            <Button type="ghost" icon={<EyeIcon />} onClick={handleOnPreview}>
              {t('preview')}
            </Button>
            <div className="w-1 h-6 bg-divider" style={{ width: 1 }} />
          </>
        )}
        {onDownload && (
          <>
            <TranslationsSelect
              type="primary"
              answersSheetId={answersSheetId}
              answersSheetLanguages={
                answersSheetDocument && answersSheetDocument.language
                  ? [answersSheetDocument.language]
                  : []
              }
              onChange={setAnswersSheetLanguage}
              value={answersSheetLanguage}
              valueAsLanguage
            />
            <Button
              type="ghost"
              icon={<DownloadIcon />}
              onClick={() => handleOnDownload('patient')}
            >
              {t('patientPDF')}
            </Button>
            <Button
              icon={<DownloadIcon />}
              onClick={() => handleOnDownload('provider')}
            >
              {t('providerPDF')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

DownloadCard.propTypes = {
  answersSheet: PropTypes.instanceOf(AnswersSheet),
  onPreview: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

DownloadCard.defaultProps = {
  answersSheet: {},
};

const PatientDocuments = ({ recipientId, answersSheets, onPreview }) => {
  const { t } = useTranslation();
  const awsRum = useRum();
  const dispatch = useDispatch();
  const [customPDFs, setCustomPDFs] = useState([]);
  console.log({ recipientId, answersSheets });

  const onDownload = ({
    references,
    audience,
    language,
    templateId,
    reportType = 'default',
  }) => {
    const startTime = new Date().getTime();
    dispatch(
      callMethod(apiZedocGetCustomPDF, {
        references,
        audience,
        language,
        reportType,
        templateId,
      }),
    )
      .then((url) => {
        awsRum?.recordEvent('com.zedoc.rum.get_pdf_for_answers_sheet', {
          duration: new Date().getTime() - startTime,
        });

        const blob = new Blob([url], {
          type: 'application/pdf',
        });

        // Generate filename with reference value (always one value)
        const referenceValue = references ? Object.values(references)[0] : '';
        const referenceStr = referenceValue ? `-${referenceValue}` : '';
        const reportTypeStr = reportType === 'default' ? 'default' : 'custom';

        saveAs(blob, `response-${reportTypeStr}${referenceStr}.pdf`);
      })
      .catch(notifyError());
  };

  useEffect(() => {
    if (!recipientId) return;

    if (enableCustomReports) {
      dispatch(
        callMethod(apiZedocGetCompletedMilestoneListWithCustomPDF, {
          recipientId,
        }),
      )
        .then((data) => setCustomPDFs(data || []))
        .catch(notifyError);
    }
  }, [recipientId, dispatch]);

  return (
    <div className="stack-6">
      {customPDFs?.length > 0 && (
        <div className="stack-4">
          <p className="text-lg font-semibold px-4">{t('customPDFs')}</p>
          <div className="border border-divider rounded-md stack-4 p-4">
            {customPDFs.map((pdf, idx) => (
              <>
                {idx !== 0 && <hr className="border-divider" />}

                <CustomPDF
                  key={pdf.recipientId}
                  title={pdf.pdfReportName}
                  projectName={pdf.projectName}
                  milestoneName={pdf.milestoneName}
                  completedAt={pdf.milestoneCompletedAt}
                  answersSheets={pdf.answersSheets}
                  recipientId={pdf.recipientId}
                  activityId={pdf.activityId}
                  // eslint-disable-next-line no-alert
                  onDownload={() =>
                    onDownload({
                      references: { activityId: pdf.activityId },
                      templateId: pdf.pdfReportId,
                      reportType: pdf.pdfReportType,
                    })
                  }
                />
              </>
            ))}
          </div>
        </div>
      )}
      <div className="stack-4">
        <p className="text-lg font-semibold px-4">{t('questionnaires')}</p>
        <div className="border border-divider rounded-md stack-4 p-4">
          {answersSheets.map((answersSheet, idx) => (
            <>
              {idx !== 0 && <hr className="border-divider" />}
              <DownloadCard
                key={answersSheet._id}
                answersSheet={answersSheet}
                onPreview={onPreview}
                onDownload={onDownload}
              />
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

PatientDocuments.propTypes = {
  answersSheets: PropTypes.arrayOf(PropTypes.instanceOf(AnswersSheet)),
  onPreview: PropTypes.func,
};

PatientDocuments.defaultProps = {
  answersSheets: [],
  onPreview: null,
};

export default PatientDocuments;
